import React, { useState, useCallback, useMemo } from 'react';
import { message } from 'antd';
import CustomModal from '../../../../../../../v2-components/Antd/Modal';
import { Service } from '../../../../../service';
import { GetLocalStorageValue } from '../../../../../../../_config/utils/get-local-storage-value.util';
import Step1ProductSelection from './steps/Step1ProductSelection';
import Step2DeliveryType from './steps/Step2DeliveryType';
import Step3DeliveryDatePrice from './steps/Step3DeliveryDatePrice';
import Step4PaymentProof from './steps/Step4PaymentProof';
import './style.scss';

const INITIAL_STATE = {
  // Step 1 - Selected products
  selectedProducts: [],
  productQuantities: {},
  
  // Step 2 - Delivery type
  deliveryType: null, // 'delivery' | 'pickup'
  
  // Address data (for delivery)
  addressData: {
    region: null,
    regionName: '',
    commune: null,
    communeName: '',
    street: '',
    streetNumber: '',
    housingType: null, // 'house' | 'apartment'
    floorDeptTower: '',
    reference: '',
    isValidated: false,
    validatedAddress: null,
    coordinates: null,
  },
  
  // Step 3 - Delivery date and prices
  deliveryDate: null,
  deliveryDates: [],
  orderMainCalculation: null,
  originalPrice: 0,
  finalPrice: 0,
  shippingCost: 0,
  subTotalAmount: 0,
  totalAmount: 0,
  
  // Step 4 - Payment proof
  paymentProof: null,
  paymentProofPreview: null,
};

export default function PurchaseModal({ 
  open, 
  onClose, 
  quotationData,
  quotationId,
  onSuccess 
}) {
  const [currentStep, setCurrentStep] = useState(0);
  const [purchaseData, setPurchaseData] = useState(INITIAL_STATE);
  const [isLoading, setIsLoading] = useState(false);

  // Get all products from quotation
  const allProducts = useMemo(() => {
    const commercialProducts = (quotationData?.commercial_products || []).map(p => ({
      ...p,
      type: 'commercial',
      id: p.id,
      name: p.product_detail?.editable_name || p.editable_name || 'Sin nombre',
      laboratory: p.product_detail?.laboratory || p.laboratory || '-',
      sku: p.id_pharol || p.sku || '-',
      image: p.product_detail?.original_image || p.image || '',
      unitPrice: p.quotation_price?.final_unit_price || p.product_detail?.promotion_price || p.promotion_price || p.price || 0,
      quantity: p.quantity || 1,
    }));

    const magistralProducts = (quotationData?.magistral_products || []).map(p => ({
      ...p,
      type: 'magistral',
      id: p.id,
      name: p.name || 'Sin nombre',
      laboratory: p.laboratory || '-',
      sku: p.code_internal || '-',
      image: p.image_url || '',
      unitPrice: p.quotation_price?.final_unit_price || p.final_price_unit || p.price_unit || 0,
      quantity: p.quantity || 1,
    }));

    const magistralPrepare = (quotationData?.magistral_products_prepare || []).map(p => ({
      ...p,
      type: 'magistral_prepare',
      id: p.id,
      name: p.name || 'Sin nombre',
      laboratory: p.laboratory || '-',
      sku: p.code_internal || '-',
      image: p.image_url || '',
      unitPrice: p.quotation_price?.final_unit_price || p.final_price_unit || p.price_unit || 0,
      quantity: p.quantity || 1,
    }));

    return [...commercialProducts, ...magistralProducts, ...magistralPrepare];
  }, [quotationData]);

  // Initialize selected products with all products when modal opens
  React.useEffect(() => {
    if (open && allProducts.length > 0) {
      const initialQuantities = {};
      allProducts.forEach(p => {
        initialQuantities[p.id] = p.quantity;
      });
      
      setPurchaseData(prev => ({
        ...prev,
        selectedProducts: allProducts.map(p => p.id),
        productQuantities: initialQuantities,
      }));
    }
  }, [open, allProducts]);

  // Calculate original price based on selected products
  const calculateOriginalPrice = useCallback(() => {
    return allProducts
      .filter(p => purchaseData.selectedProducts.includes(p.id))
      .reduce((acc, p) => {
        const qty = purchaseData.productQuantities[p.id] || p.quantity;
        return acc + (p.unitPrice * qty);
      }, 0);
  }, [allProducts, purchaseData.selectedProducts, purchaseData.productQuantities]);

  // Update purchaseData
  const updatePurchaseData = useCallback((updates) => {
    setPurchaseData(prev => ({
      ...prev,
      ...updates,
    }));
  }, []);

  // Get token for API calls
  const { token } = GetLocalStorageValue('userInfo') || {};

  // Call manualPurchase API when going from Step2 to Step3
  const fetchManualPurchaseData = useCallback(async () => {
    setIsLoading(true);
    try {
      const typeRetirement = purchaseData.deliveryType === 'pickup' ? 'retiro' : 'domicilio';
      const typeHouse = purchaseData.addressData.housingType === 'house' ? 'casa' : 'departamento';
      
      const body = {
        quotation_magistral_id: quotationId,
        products: purchaseData.selectedProducts.map(productId => {
          const product = allProducts.find(p => p.id === productId);
          return {
            id: productId,
            quantity: purchaseData.productQuantities[productId] || product?.quantity || 1,
          };
        }),
        type_retirement: typeRetirement,
        delivery_info: purchaseData.deliveryType === 'delivery' ? {
          lat: purchaseData.addressData.coordinates?.lat || 0,
          lng: purchaseData.addressData.coordinates?.lng || 0,
          street: purchaseData.addressData.street || '',
          street_number: purchaseData.addressData.streetNumber || '',
          commune: purchaseData.addressData.communeName || '',
          region: purchaseData.addressData.regionName || '',
          type_house: typeHouse,
          reference: purchaseData.addressData.reference || null,
          floor: purchaseData.addressData.floorDeptTower || '',
        } : null,
      };

      const response = await new Service().manualPurchaseCalculation(body, token);
      console.log('manualPurchaseCalculation response:', purchaseData.addressData);
      
      // Format delivery dates for select options
      const formattedDates = (response.delivery_dates || []).map(date => ({
        label: date,
        value: date,
      }));

      updatePurchaseData({
        deliveryDates: formattedDates,
        orderMainCalculation: response.order_main_calculation,
        subTotalAmount: response.sub_total_amount || 0,
        shippingCost: response.delivery_amount || 0,
        totalAmount: response.total_amount || 0,
        originalPrice: response.sub_total_amount || 0,
        finalPrice: response.sub_total_amount || 0,
      });

      return true;
    } catch (error) {
      console.error('Error calling manualPurchase:', error);
      message.error('Error al obtener información de entrega');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [quotationId, purchaseData, allProducts, token, updatePurchaseData]);

  // Handle step navigation
  const handleNext = useCallback(async () => {
    if (currentStep === 0) {
      // When going to step 1, calculate original price
      const originalPrice = calculateOriginalPrice();
      updatePurchaseData({ 
        originalPrice,
        finalPrice: originalPrice
      });
      setCurrentStep(prev => prev + 1);
    } else if (currentStep === 1) {
      // When going from step 2 to step 3, call manualPurchase API
      const success = await fetchManualPurchaseData();
      if (success) {
        setCurrentStep(prev => prev + 1);
      }
    } else {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, calculateOriginalPrice, updatePurchaseData, fetchManualPurchaseData]);

  const handleBack = useCallback(() => {
    setCurrentStep(prev => prev - 1);
  }, []);

  // Handle modal close
  const handleClose = useCallback(() => {
    setCurrentStep(0);
    setPurchaseData(INITIAL_STATE);
    onClose();
  }, [onClose]);

  // Handle final save
  const handleSave = useCallback(async () => {
    setIsLoading(true);
    try {
      // For pickup, use the first available date if no date is selected
      const selectedDate = purchaseData.deliveryDate || 
        (purchaseData.deliveryDates?.[0]?.value || purchaseData.deliveryDates?.[0]);

      // Step 1: Call manualPurchase to finalize and get voucher upload URL
      const purchaseBody = {
        order_main_calculation: purchaseData.orderMainCalculation,
        total_amount_fixed: purchaseData.finalPrice + purchaseData.shippingCost,
        delivery_date_selected: selectedDate,
      };
      console.log('Final purchase body:', purchaseBody);
      console.log('Final purchase data:', purchaseData);

      const purchaseResponse = await new Service().manualPurchase(purchaseBody, token);
      
      // Step 2: Upload payment proof to the signed URL
      if (purchaseResponse.url_voucher && purchaseData.paymentProof) {
        await new Service().uploadToSignedUrl(
          purchaseResponse.url_voucher, 
          purchaseData.paymentProof
        );
      }

      message.success('Compra finalizada exitosamente');
      
      if (onSuccess) {
        onSuccess();
      }
      handleClose();
    } catch (error) {
      console.error('Error saving purchase:', error);
      message.error('Error al finalizar la compra');
    } finally {
      setIsLoading(false);
    }
  }, [purchaseData, onSuccess, handleClose]);

  // Get selected products for display
  const selectedProductsData = useMemo(() => {
    return allProducts.filter(p => purchaseData.selectedProducts.includes(p.id));
  }, [allProducts, purchaseData.selectedProducts]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Step1ProductSelection
            products={allProducts}
            selectedProducts={purchaseData.selectedProducts}
            productQuantities={purchaseData.productQuantities}
            onSelectionChange={(selected) => updatePurchaseData({ selectedProducts: selected })}
            onQuantityChange={(productId, quantity) => 
              updatePurchaseData({ 
                productQuantities: { 
                  ...purchaseData.productQuantities, 
                  [productId]: quantity 
                } 
              })
            }
            onNext={handleNext}
          />
        );
      case 1:
        return (
          <Step2DeliveryType
            deliveryType={purchaseData.deliveryType}
            addressData={purchaseData.addressData}
            onDeliveryTypeChange={(type) => updatePurchaseData({ deliveryType: type })}
            onAddressDataChange={(data) => updatePurchaseData({ 
              addressData: { ...purchaseData.addressData, ...data } 
            })}
            onBack={handleBack}
            onNext={handleNext}
            isLoading={isLoading}
          />
        );
      case 2:
        return (
          <Step3DeliveryDatePrice
            deliveryType={purchaseData.deliveryType}
            deliveryDate={purchaseData.deliveryDate}
            deliveryDates={purchaseData.deliveryDates}
            originalPrice={purchaseData.originalPrice}
            finalPrice={purchaseData.finalPrice}
            shippingCost={purchaseData.shippingCost}
            subTotalAmount={purchaseData.subTotalAmount}
            totalAmount={purchaseData.totalAmount}
            selectedProducts={selectedProductsData}
            productQuantities={purchaseData.productQuantities}
            onDeliveryDateChange={(date) => updatePurchaseData({ deliveryDate: date })}
            onFinalPriceChange={(price) => updatePurchaseData({ finalPrice: price })}
            onBack={handleBack}
            onNext={handleNext}
            isLoading={isLoading}
          />
        );
      case 3:
        return (
          <Step4PaymentProof
            paymentProof={purchaseData.paymentProof}
            paymentProofPreview={purchaseData.paymentProofPreview}
            onPaymentProofChange={(proof, preview) => updatePurchaseData({ 
              paymentProof: proof, 
              paymentProofPreview: preview 
            })}
            onBack={handleBack}
            onSave={handleSave}
            isLoading={isLoading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <CustomModal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={800}
      className="purchase-modal"
      destroyOnClose
    >
      <div className="purchase-modal-content">
        <div className="purchase-modal-step-content">
          {renderStepContent()}
        </div>
      </div>
    </CustomModal>
  );
}
